// userKeyGenerator.js
// Generate a dynamic user key that remains consistent for the same phone number
// Uses Supabase for persistent storage across serverless functions

import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabase = createClient(
  'https://omgwcmqastewylltshdu.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9tZ3djbXFhc3Rld3lsbHRzaGR1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzA0Mjc5MzIsImV4cCI6MjA0NjAwMzkzMn0.Dd82cSB_T2fKSQgBW4oQtFtAMFVXC1gkjW78FaVIoF8'
);

// In-memory cache with TTL and size limits
const userKeyCache = new Map();
const pendingRequests = new Map(); // Prevent race conditions
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes
const MAX_CACHE_SIZE = 1000; // Prevent memory leaks

// Generate random userKey
function generateRandomUserKey() {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  
  for (let i = 0; i < 12; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  
  return result;
}

// Clean expired cache entries
function cleanExpiredCache() {
  const now = Date.now();
  for (const [key, value] of userKeyCache.entries()) {
    if (now - value.timestamp > CACHE_TTL) {
      userKeyCache.delete(key);
    }
  }
}

// Manage cache size
function manageCacheSize() {
  if (userKeyCache.size > MAX_CACHE_SIZE) {
    // Remove oldest entries
    const entries = Array.from(userKeyCache.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    const toRemove = entries.slice(0, Math.floor(MAX_CACHE_SIZE / 4));
    toRemove.forEach(([key]) => userKeyCache.delete(key));
  }
}

export async function generateUserKey(phoneNumber) {
  if (!phoneNumber) {
    throw new Error('Phone number is required to generate userKey');
  }

  // Clean expired cache entries periodically
  if (Math.random() < 0.1) { // 10% chance to clean
    cleanExpiredCache();
  }

  // Check in-memory cache first (faster)
  const cachedEntry = userKeyCache.get(phoneNumber);
  if (cachedEntry && (Date.now() - cachedEntry.timestamp) < CACHE_TTL) {
    return cachedEntry.userKey;
  }

  // Check if there's already a pending request for this phone number
  if (pendingRequests.has(phoneNumber)) {
    return await pendingRequests.get(phoneNumber);
  }

  // Create a promise for this request to prevent race conditions
  const requestPromise = (async () => {
    try {
      // Check if userKey exists in database
      const { data, error } = await supabase
        .from('subscriptions')
        .select('user_key')
        .eq('phone_number', phoneNumber)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error fetching userKey:', error);
        throw new Error('Failed to fetch userKey from database');
      }

      let userKey;

      if (data && data.user_key) {
        // Use existing userKey from database
        userKey = data.user_key;
      } else {
        // Generate new userKey
        userKey = generateRandomUserKey();

        // Store in database with proper error handling
        const { error: updateError } = await supabase
          .from('subscriptions')
          .update({ user_key: userKey })
          .eq('phone_number', phoneNumber);

        if (updateError) {
          console.error('Error storing userKey:', updateError);
          // If update fails, user might not exist, so we fallback to temporary key
          console.warn('Using temporary userKey due to database error');
          return userKey; // Don't cache this temporary key
        }
      }

      // Cache in memory for this instance
      manageCacheSize();
      userKeyCache.set(phoneNumber, {
        userKey,
        timestamp: Date.now()
      });
      
      return userKey;

    } catch (error) {
      console.error('Error in generateUserKey:', error);
      // Fallback: generate temporary userKey without database storage
      const tempKey = generateRandomUserKey();
      console.warn('Using temporary userKey due to error:', tempKey);
      return tempKey;
    } finally {
      // Remove from pending requests
      pendingRequests.delete(phoneNumber);
    }
  })();

  // Store the promise to prevent race conditions
  pendingRequests.set(phoneNumber, requestPromise);
  
  return await requestPromise;
}

export async function getUserKey(phoneNumber) {
  return await generateUserKey(phoneNumber);
}

// Optional: Clear userKey from database and cache
export async function clearUserKey(phoneNumber) {
  if (!phoneNumber) return;

  try {
    const { error } = await supabase
      .from('subscriptions')
      .update({ user_key: null })
      .eq('phone_number', phoneNumber);

    if (error) {
      console.error('Error clearing userKey:', error);
      throw new Error('Failed to clear userKey from database');
    }

    // Clear from memory cache
    userKeyCache.delete(phoneNumber);
    
    // Clear any pending requests
    pendingRequests.delete(phoneNumber);
    
  } catch (error) {
    console.error('Error in clearUserKey:', error);
    throw error;
  }
}

// Optional: Clear all cached keys (for maintenance)
export function clearAllCachedKeys() {
  userKeyCache.clear();
  pendingRequests.clear();
}

// Optional: Get cache statistics (for debugging)
export function getCacheStats() {
  return {
    cacheSize: userKeyCache.size,
    pendingRequests: pendingRequests.size,
    maxCacheSize: MAX_CACHE_SIZE,
    cacheTTL: CACHE_TTL
  };
}
